import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useViewport } from '@/hooks'
import { useSignOut } from '@/hooks'
import { useChangeUiLanguage } from '@/hooks/useChangeUiLanguage'
import { RootState, useTypedSelector, useTypedDispatch } from '@/store'
import { useListLanguagesQuery } from '@/services/api/tenant/languages'
import * as amplitude from '@amplitude/analytics-browser'
import Cookies from 'js-cookie'
import { config as appConfig } from '@/core'

type Language = {
  tag: string
  display_name: string
}

export const useMobileNavBarLogic = () => {
  const { i18n } = useTranslation('general')
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null)
  const [showLanguageInMenu, setShowLanguageInMenu] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 390 : false,
  )
  const user = useTypedSelector((state: RootState) => state.authSlice.user)
  const tenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const location = useLocation()
  const viewport = useViewport()
  const dispatch = useTypedDispatch()
  const signOutAction = useSignOut()
  const { changeUiLanguage } = useChangeUiLanguage()
  const { data: languages = [] } = useListLanguagesQuery({ displayInUi: true }) as { data: Language[] }

  useEffect(() => {
    setMenuAnchor(null)
    setLanguageMenuAnchor(null)
  }, [location.pathname])

  useEffect(() => {
    setMenuAnchor(null)
    setLanguageMenuAnchor(null)
  }, [viewport])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const query = window.matchMedia('(min-width: 390px)')

    const handleViewportChange = () => {
      setShowLanguageInMenu(!query.matches)
    }

    query.addEventListener('change', handleViewportChange)

    return () => {
      query.removeEventListener('change', handleViewportChange)
    }
  }, [])

  const handleDrawerToggle = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(menuAnchor ? null : event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleNavigate = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()

    const path = event.currentTarget.getAttribute('data-path') || event.currentTarget.getAttribute('href')

    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur()
    }

    setMenuAnchor(null)

    if (path) {
      setTimeout(() => {
        window.history.pushState({}, '', path)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }, 10)
    }
  }

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget)
  }

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null)
  }

  const handleLanguageChange = async (languageCode: string, event: React.MouseEvent<HTMLElement>) => {
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur()
    }

    setLanguageMenuAnchor(null)
    setMenuAnchor(null)

    await changeUiLanguage(languageCode)
  }

  const isAccessTokenValid = () => {
    const expiryTime = Cookies.get('access_token_expiry')
    if (!expiryTime) return false

    const currentTime = new Date().getTime()
    return currentTime < Number(expiryTime)
  }

  const signOut = (event: React.MouseEvent): void => {
    event.preventDefault()
    handleMenuClose()
    if (!isAccessTokenValid()) {
      amplitude.reset()
      dispatch(signOutAction)
      window.location.href = `${appConfig.api.origin}/logout?redirect_url=${encodeURIComponent(
        appConfig.api.redirect_url,
      )}`
    }
  }

  return {
    user,
    tenant,
    location,
    languages,
    menuAnchor,
    languageMenuAnchor,
    currentLanguage: i18n.language,
    showLanguageInMenu,
    handleDrawerToggle,
    handleMenuClose,
    handleNavigate,
    handleLanguageMenuOpen,
    handleLanguageMenuClose,
    handleLanguageChange,
    signOut,
  }
}
